import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Galleria 8 slot — mapping 1:1, anti-duplicazione totale.
const GALLERY_SLOTS = 8;
const GALLERY_PHOTOS = [
  // 01 — Zago seduta in tribuna sorridente con palla Mizuno e due trofei (full-width)
  "/images/assets/m3v7gor3_20220531_14-44-36_z62_4148-980x1473.jpg",
  // 02 — Ritratto frontale in tribuna, sguardo deciso
  "/images/assets/bwb3gzzm_20220531_14-26-24_z62_4058-980x1473.jpg",
  // 03 — Dettaglio ginocchiere Mizuno, scarpe Mizuno, palla firmata e trofei
  "/images/assets/e3fqjtv7_20220531_14-45-48_z62_4157-980x1473.jpg",
  // 04 — Still life maglia ZAGO #13 + palloni dorati + coppa
  "/images/assets/sh94s6x2_20220531_14-53-37_z62_4190.jpg",
  // 05 — Caffè al bar storico, dettaglio felpa Mizuno Union Volley Pinerolo (full-width)
  "/images/assets/7m251f7e_20220531_09-04-35_z61_0940-980x1469.jpg",
  // 06 — Ritratto al bar con medaglia MVP Serie A2 al collo, divano rosso
  "/images/assets/98wz9nvx_20220531_09-07-37_z61_0958-980x1470.jpg",
  // 07 — In negozio di dischi, braccia incrociate, divisa Pinerolo + Mizuno
  "/images/assets/3or7a492_20220531_10-03-50_z61_1193-980x1470.jpg",
  // 08 — In negozio di dischi, posa frontale, tuta blu Mizuno Pinerolo
  "/images/assets/sbue68q2_20220531_10-04-23_z61_1213-980x1473.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Mizuno × Valentina Zago — Pinerolo, luglio 2022 — Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

// Layout magazine: 1 full · 2-4 quarter · 5 full · 6-8 quarter
const SLOT_LAYOUT = [
  "col-span-4 md:col-span-12",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-12",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
  "col-span-4 md:col-span-4",
];

const WorkMizunoValentinaZago = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/mizuno-valentina-zago");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Mizuno × Valentina Zago", url: "/works/mizuno-valentina-zago" },
  ]);

  return (
    <>
      <Seo
        title="Mizuno × Valentina Zago: 590K impressions, 10+ anni | OnSports Case"
        description="Brand legacy Mizuno × Valentina Zago: 590.000+ impressions, partnership decennale. OnSports Content & IP Studio per l'atleta azzurra di pallavolo."
        path="/works/mizuno-valentina-zago"
        image="/images/assets/m3v7gor3_20220531_14-44-36_z62_4148-980x1473.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/mizuno-valentina-zago/hero.gif"
        imageAlt="Mizuno × Valentina Zago — Reach Beyond"
        eyebrow="● Case study · Cultural Impact · Content & IP Studio · Legacy"
        titleLines={["Una giocatrice. Una filosofia.", "Un OLTRE."]}
        highlightWord="OLTRE."
        subtitle="Mizuno ci ha chiesto di costruire un capitolo della propria serie editoriale Reach Beyond attorno a Valentina Zago, opposto mancino dell'Union Volley Pinerolo. Una storia di metodo costruita su un dato silenzioso: Mizuno è sponsor tecnico di Valentina da oltre dieci anni. Reach Beyond non è un claim per lei — è la traduzione italiana di una partnership che ha attraversato un decennio di pallavolo."
        meta="2022  ·  Cliente — Mizuno  ·  Categoria — Brand Legacy & Storytelling"
      />

      {/* SEZ. 2 — IL VIDEO (full-width wow moment) */}
      <section className="bg-dodgers py-12 md:py-16" data-testid="section-video">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <RevealOnScroll>
            <div className="overflow-hidden shadow-2xl" data-testid="wistia-zago">
              <WistiaPlayer mediaId="82js5yjnhb" aspect={1.7777777777777777} />
            </div>
          </RevealOnScroll>
          <p
            className="mt-4 text-galactico/70 text-sm text-center"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
          >
            Reach Beyond — Il ritratto editoriale di Valentina Zago, prodotto
            per i canali Mizuno.
          </p>
        </div>
      </section>

      {/* SEZ. 3 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Reach Beyond è la serie editoriale con cui Mizuno racconta
                    — in voce italiana, sui canali italiani — il significato
                    della filosofia Kai Zen, il principio giapponese del
                    miglioramento continuo che sta al centro della cultura del
                    brand. Non è una campagna prodotto. È un dispositivo di
                    brand legacy, pensato per coltivare nel tempo
                    l'associazione tra Mizuno e l'idea di disciplina,
                    costanza, ambizione.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Per il secondo capitolo della serie Mizuno ci ha chiesto di
                    costruire un ritratto editoriale di Valentina Zago —
                    opposto mancino dell'Union Volley Pinerolo, nata a Padova
                    nel 1990, sponsorizzata da Mizuno da oltre dieci anni.
                    Quella stagione (2021/22) Zago aveva guidato la squadra
                    alla promozione in Serie A1 conquistando anche il premio di
                    MVP del campionato di Serie A2 femminile.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.25}>
                  <p>
                    Abbiamo costruito il progetto attorno a una scelta: non un
                    video di highlight, ma un racconto lungo e composto sulla
                    sua idea di "andare oltre". Filosofia personale, metodo di
                    allenamento, momenti di crisi della carriera, ritorno alla
                    vittoria. Il pezzo è andato sui canali ufficiali Mizuno e
                    in parallelo è stato amplificato editorialmente sulla
                    community Problemi di Volley, dove ha vissuto come articolo
                    lungo e contenuti social derivati.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">
                      Reach Beyond — Episodio Valentina Zago
                    </SchedaRow>
                    <SchedaRow label="Protagonista">
                      Valentina Zago — Opposto, Union Volley Pinerolo
                    </SchedaRow>
                    <SchedaRow label="Cliente">Mizuno</SchedaRow>
                    <SchedaRow label="Legacy">
                      10+ anni di partnership Mizuno-Zago
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Idea creativa · Produzione foto e video<br />
                      Seeding cross-canale
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Format">
                      Serie editoriale Reach Beyond
                    </SchedaRow>
                    <SchedaRow label="Anno">2022</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Tradurre Kai Zen in una storia italiana.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Kai Zen è un concetto culturale giapponese — miglioramento
                  continuo, costanza incrementale, disciplina del piccolo
                  passo. Per un brand giapponese come Mizuno è il principio
                  fondante. Per un pubblico italiano di sport, è un'idea
                  astratta che richiede una traduzione concreta. La sfida non
                  era spiegarla — era incarnarla in una persona, in una
                  stagione, in un gesto tecnico.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La cornice editoriale era Reach Beyond, la serie con cui
                  Mizuno aveva deciso di lavorare sulla propria legacy nel
                  volley italiano attraverso ritratti di atleti sponsorizzati.
                  Il brief era esigente sotto due profili: il pezzo doveva
                  essere bello indipendentemente dal brand (altrimenti viene
                  letto come sponsorizzato e perde valore), e doveva al
                  contempo restare riconoscibile come capitolo della serie —
                  voce, ritmo, codici visivi coerenti con gli altri episodi.
                  Era un esercizio di equilibrio tra autorialità e coerenza di
                  brand.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — LA STORIA · PULL QUOTE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-storia">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>La storia</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Andare oltre, secondo Valentina Zago.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[720px] opacity-90">
                  Abbiamo incontrato Valentina a Pinerolo, la città in cui
                  giocava da diverse stagioni e in cui era riconosciuta per
                  strada. Le abbiamo chiesto di raccontare cosa significhi per
                  lei andare oltre, e la risposta è arrivata con una precisione
                  che ha definito tutto il tono editoriale del pezzo:
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <figure
              className="mx-auto max-w-[860px] py-10 md:py-16 border-y border-dodgers/15"
              data-testid="zago-quote"
            >
              <blockquote
                className="font-display text-dodgers leading-[1.05] tracking-tight"
                style={{ fontSize: "clamp(26px, 4vw, 44px)" }}
              >
                <span className="text-lime">"</span>Andare oltre vuol dire non
                mollare mai nei momenti di difficoltà. Trovare quella spinta
                per uscire da un momento di crisi. Oppure per tenere bene a
                mente un momento buono e portarlo il più avanti possibile.
                <span className="text-lime">"</span>
              </blockquote>
              <figcaption className="mt-6 font-condensed uppercase tracking-widest text-[13px] opacity-75">
                — Valentina Zago, Opposto
              </figcaption>
            </figure>
          </RevealOnScroll>

          <RevealOnScroll delay={0.12}>
            <p className="mt-10 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px] opacity-90">
              È la voce dell'atleta che ha passato una stagione 2013/14 da
              miglior battitore di Serie A1 (42 ace in 58 set a Casalmaggiore)
              e poi anni in cui il servizio l'ha tradita, e ha dovuto
              ricostruirselo. Reach Beyond non è il momento del trionfo. È
              quello che fai dentro le crisi.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre parti. Un'unica voce.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo gestito il progetto end-to-end con tre filiere
                  coordinate: produzione visiva, distribuzione coordinata
                  cross-canale, integrazione editoriale con la community
                  Problemi di Volley.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Idea & concept"
                body="Costruzione del concept editoriale del singolo episodio: scelta dell'angolo narrativo (Reach Beyond come metodo personale dell'atleta, non come slogan di brand), scaletta delle interviste, definizione delle inquadrature chiave, allineamento del tone of voice con la serie globale Mizuno."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Produzione video & photo"
                body="Produzione integrale del video editoriale a Pinerolo, photo shooting professionale degli allenamenti e dei ritratti, post-produzione con grading coerente alla brand identity Mizuno. Versioning del materiale per i formati richiesti dai diversi canali di distribuzione."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Articolo editoriale"
                body="Pubblicazione di un articolo lungo sulla community Problemi di Volley con la storia integrale dell'atleta. La forma scritta permette di andare oltre i 60-90 secondi del video, dare voce diretta alle parole di Valentina, lavorare sul tempo lungo della lettura — costruendo affezione, non solo awareness."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Seeding cross-canale"
                body="Distribuzione coordinata su due livelli: i canali Mizuno (sito brand, social ufficiali Italia) come centro istituzionale del progetto, la community Problemi di Volley come amplificatore verticale presso il pubblico volley italiano. Calendario di pubblicazione costruito per generare due picchi di attenzione consecutivi e prolungare la curva di vita del contenuto."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Pinerolo, luglio 2022.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione del photo shooting realizzato a Pinerolo, dove
                  Valentina Zago giocava in quel periodo: ritratti, momenti
                  dell'allenamento, dettagli del gesto tecnico, atmosfera del
                  palazzetto.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-4 md:grid-cols-12 gap-3"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const wrapperClass = SLOT_LAYOUT[i];
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 7) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "4 / 3" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 7) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    style={{ aspectRatio: "4 / 3" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 8 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Brand legacy non è una metrica. È un effetto cumulativo.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="590.000+" label="Impressions del contenuto sui canali Mizuno" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="19.000+" label="Interazioni generate" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="10+" label="Anni di partnership Mizuno-Zago (contesto del progetto)" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="2" label="Canali editoriali · Mizuno + Problemi di Volley di amplificazione" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati aggregati di campagna sui canali Mizuno + amplificazione
            editoriale Problemi di Volley. Progetto realizzato a luglio 2022,
            parte della serie editoriale "Reach Beyond" del brand.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Brand legacy come investimento di lungo termine
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Reach Beyond non è pensato per generare conversione immediata
                  — non c'è una scarpa in lancio, non c'è un funnel di vendita.
                  È pensato per accumulare significato culturale attorno al
                  brand Mizuno nel mercato volley italiano. È un investimento
                  di pazienza, che paga in dividendi lenti: top-of-mind quando
                  si compra un nuovo paio di scarpe, preferenza relativa
                  quando ci sono cinque brand a confronto.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Una decennale partnership al servizio del racconto
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il dato che la sponsorizzazione Mizuno-Zago durasse da oltre
                  dieci anni al momento del progetto non era un dettaglio
                  biografico. Era la prova vivente che la filosofia Kai Zen
                  del brand non è retorica — è una pratica che il brand applica
                  anche al modo in cui sceglie e mantiene le proprie relazioni
                  con gli atleti.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Voce autoriale, non endorsement
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  La quote di Valentina Zago al centro del progetto — quella
                  sul significato del momento di crisi — è arrivata davvero,
                  in conversazione, davanti a una telecamera. Non era una
                  linea di copy preparata. Quando i contenuti sponsorizzati
                  hanno questo livello di autenticità, smettono di essere
                  percepiti come pubblicità. Diventano editoriali con un brand
                  al margine — esattamente quello che la serie Reach Beyond
                  cercava.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Cliente">Mizuno</SchedaRow>
                <SchedaRow label="Progetto">
                  Reach Beyond — Episodio Valentina Zago
                </SchedaRow>
                <SchedaRow label="Protagonista">
                  Valentina Zago — Opposto
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Idea creativa · Produzione video e foto<br />
                  Articolo editoriale · Seeding cross-canale
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Sponsorship &amp; Impact Programs
                </SchedaRow>
                <SchedaRow label="Canali">
                  Mizuno (corporate) · Problemi di Volley (community)
                </SchedaRow>
                <SchedaRow label="Anno">2022</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai una partnership di lungo termine che vorresti far esistere
              come storia? Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Le sponsorship di lunga durata sono il patrimonio più
              sottovalutato del marketing sportivo italiano. Trasformarle in
              legacy storytelling — non solo in visibilità ricorrente — è uno
              dei nostri lavori più gratificanti. Brand journalism, ritratti
              editoriali, format proprietari su community verticali.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkMizunoValentinaZago;
